import * as THREE from 'three'
import type { GSplatSogsData } from '@/plugins/gsplat/core/GSplatSogsData'
import { GSplatResourceBase, type TextureSize } from '@/plugins/gsplat/lod/GSplatResourceBase'
import type { IGSplatMaterial, IGSplatSogsResource } from '@/plugins/gsplat/types/ResourceTypes'
import { syncTextureUploadVersion } from '@/plugins/gsplat/utils/syncTextureUploadVersion'
import { getTextureImage } from '@/plugins/gsplat/utils/textureImage'
import { getLoggerManager } from '@/shared/logging'
import type { SogsMetaV2 } from './SogsParser'

const log = getLoggerManager().getLogger('gsplat:loader')

/**
 * SOGS 资源类
 * 对应 PlayCanvas gsplat-sogs-resource.js
 *
 * 职责：
 * 1. 管理 SOGS 压缩格式的 GPU 纹理
 * 2. 配置材质的 SOGS 专用 uniforms
 * 3. 设置 SOGS 专用的 shader defines
 *
 * 注意：
 * - GSplatSogsData 内部已经处理了上下文恢复（packGpuMemory/packShMemory）
 * - GSplatSogsResource 不需要额外的上下文恢复逻辑
 * - Codebook 纹理需要在上下文恢复时重建
 */
export class GSplatSogsResource extends GSplatResourceBase implements IGSplatSogsResource {
  /**
   * SOGS 数据（覆盖基类类型）
   */
  declare readonly gsplatData: GSplatSogsData

  /** 获取 meta 数据（实现 IGSplatSogsResource 接口） */
  get meta(): SogsMetaV2 {
    return this.gsplatData.meta
  }

  /** Codebook 纹理缓存（用于上下文恢复） */
  private codebookTextures: THREE.DataTexture[] = []

  /** 避免 configureMaterial 每帧刷屏 */
  private loggedConfigureMaterial = false

  private _gpuReady = true

  private _gpuRestoreFailed = false

  private _sogsDestroyed = false

  private _retired = false

  private _onContextLost: (() => void) | null = null

  private _gpuReadyCallback: (() => void) | null = null

  private _gpuRestoreFailedCallback: ((error: Error) => void) | null = null

  private _gpuRestorePromise: Promise<void> | null = null

  /**
   * 获取 SH bands（兼容 GSplatResource 接口）
   */
  get shBands(): number {
    return this.gsplatData.shBands
  }

  /**
   * 获取 splat 数量（兼容 GSplatResource 接口）
   */
  get numSplats(): number {
    return this.gsplatData.numSplats
  }

  constructor(renderer: THREE.WebGLRenderer, gsplatData: GSplatSogsData) {
    super(renderer, gsplatData)

    try {
      this.preuploadTextures()

      this._onContextLost = this.onContextLost.bind(this)
      renderer.domElement.addEventListener('webglcontextlost', this._onContextLost)

      // 注册 WebGL 上下文恢复事件（重建源纹理与 codebook 纹理）
      this._onContextRestored = this.onContextRestored.bind(this)
      renderer.domElement.addEventListener('webglcontextrestored', this._onContextRestored)
    } catch (error) {
      // super() 已创建基础 geometry/material；后续 GPU 预上传失败时构造函数不会返回，
      // 因此需在此回收部分构造资源。gsplatData 的所有权仍由调用方在构造失败时回收。
      if (this._onContextLost) {
        renderer.domElement.removeEventListener('webglcontextlost', this._onContextLost)
        this._onContextLost = null
      }
      this.disposeCodebookTextures()
      super.destroy()
      throw error
    }
  }

  /**
   * 销毁资源
   */
  destroy(): void {
    if (this._sogsDestroyed) {
      return
    }
    this._sogsDestroyed = true
    this._gpuReady = false
    this._gpuReadyCallback = null
    this._gpuRestoreFailedCallback = null
    this.gsplatData.cancelSourceTextureRestore()
    if (this._onContextLost) {
      this.renderer.domElement.removeEventListener('webglcontextlost', this._onContextLost)
      this._onContextLost = null
    }

    // 销毁 codebook 纹理
    this.disposeCodebookTextures()

    // 销毁 SOGS 数据
    this.gsplatData.destroy()

    // 调用基类销毁
    super.destroy()
  }

  get gpuReady(): boolean {
    return this._gpuReady
  }

  get gpuRestoreFailed(): boolean {
    return this._gpuRestoreFailed
  }

  setGpuReadyCallback(callback: (() => void) | null): void {
    this._gpuReadyCallback = callback
  }

  setGpuRestoreFailedCallback(callback: ((error: Error) => void) | null): void {
    this._gpuRestoreFailedCallback = callback
  }

  /** 旧 WorldState 仍持有引用时延迟到最后一次 decRefCount 后再真正销毁。 */
  retireWhenUnused(): void {
    if (this._sogsDestroyed || this._retired) {
      return
    }
    this._retired = true
    this._gpuReady = false
    this._gpuReadyCallback = null
    this._gpuRestoreFailedCallback = null
    this.gsplatData.cancelSourceTextureRestore()
    if (this._onContextLost) {
      this.renderer.domElement.removeEventListener('webglcontextlost', this._onContextLost)
      this._onContextLost = null
    }
    if (this._onContextRestored) {
      this.renderer.domElement.removeEventListener('webglcontextrestored', this._onContextRestored)
      this._onContextRestored = null
    }
    if (this.refCount === 0) {
      this.destroy()
    }
  }

  override decRefCount(): void {
    super.decRefCount()
    if (this._retired && this.refCount === 0) {
      this.destroy()
    }
  }

  private disposeCodebookTextures(): void {
    for (const texture of this.codebookTextures) {
      texture.dispose()
    }
    this.codebookTextures = []
  }

  /**
   * WebGL 上下文恢复时重建 codebook 纹理
   */
  private onContextLost(): void {
    if (this._sogsDestroyed || this._retired || !this.gsplatData.sourceImagesReleased) {
      return
    }

    this._gpuReady = false
    this._gpuRestoreFailed = false
    this.gsplatData.cancelSourceTextureRestore()
  }

  private onContextRestored(): void {
    if (this._sogsDestroyed || this._retired) {
      return
    }

    this.restoreCodebookTextureData()
    if (!this.gsplatData.sourceImagesReleased) {
      return
    }

    this._gpuReady = false
    const restore = this.restoreSourceTexturesWithRetry()
    this._gpuRestorePromise = restore
    void restore
      .then(() => {
        if (this._sogsDestroyed || this._gpuRestorePromise !== restore) {
          return
        }
        // source 纹理已在 GSplatSogsData 中上传；这里同时确保 codebook 已上传。
        this.preuploadExistingTextures()
        this._gpuRestoreFailed = false
        this._gpuReady = true
        this._gpuReadyCallback?.()
      })
      .catch((error: unknown) => {
        if (this._sogsDestroyed || (error instanceof DOMException && error.name === 'AbortError')) {
          return
        }
        const finalError = error instanceof Error ? error : new Error(String(error))
        this._gpuRestoreFailed = true
        log.error(`[GSplatSogsResource] context restore failed: ${finalError.message}`)
        this._gpuRestoreFailedCallback?.(finalError)
      })
      .finally(() => {
        if (this._gpuRestorePromise === restore) {
          this._gpuRestorePromise = null
        }
      })
  }

  private async restoreSourceTexturesWithRetry(): Promise<void> {
    const maxAttempts = 2
    let lastError: unknown
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await this.gsplatData.restoreSourceTextures(this.renderer)
        return
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw error
        }
        lastError = error
        if (attempt < maxAttempts) {
          log.warn(
            `[GSplatSogsResource] context restore retry ${attempt}/${maxAttempts - 1}: ${
              error instanceof Error ? error.message : String(error)
            }`
          )
        }
      }
    }
    throw lastError instanceof Error ? lastError : new Error(String(lastError))
  }

  private restoreCodebookTextureData(): void {
    // 重建 codebook 纹理数据
    const { meta } = this.gsplatData

    this.codebookTextures.forEach((texture) => {
      // 从 meta 中重新填充数据
      const name = texture.name
      let codebook: number[] | undefined

      if (name.includes('Scales')) {
        codebook = meta.scales.codebook
      } else if (name.includes('SH0')) {
        codebook = meta.sh0.codebook
      } else if (name.includes('SHN')) {
        codebook = meta.shN?.codebook
      }

      if (codebook) {
        const data = new Float32Array(256)
        for (let i = 0; i < 256; i++) {
          data[i] = codebook[i] ?? 0
        }

        const imageData = texture.image
        if (imageData?.data) {
          ;(imageData.data as Float32Array).set(data)
          texture.needsUpdate = true
        }
      }
    })
  }

  private preuploadTextures(): void {
    const textures: Array<THREE.Texture | undefined | null> = [
      this.gsplatData.means_l,
      this.gsplatData.means_u,
      this.gsplatData.quats,
      this.gsplatData.scales,
      this.gsplatData.sh0,
      this.gsplatData.sh_labels,
      this.gsplatData.sh_centroids,
    ]

    const { meta } = this.gsplatData
    if (meta.version === 2) {
      if (meta.scales.codebook) {
        textures.push(this.createCodebookTexture('sogsScalesCodebook', meta.scales.codebook))
      }
      if (meta.sh0.codebook) {
        textures.push(this.createCodebookTexture('sogsSH0Codebook', meta.sh0.codebook))
      }
      if (meta.shN?.codebook) {
        textures.push(this.createCodebookTexture('sogsSHNCodebook', meta.shN.codebook))
      }
    }

    for (const texture of textures) {
      if (texture) {
        this.renderer.initTexture(texture)
        syncTextureUploadVersion(this.renderer, texture)
      }
    }
  }

  private preuploadExistingTextures(): void {
    const textures: Array<THREE.Texture | undefined | null> = [
      this.gsplatData.means_l,
      this.gsplatData.means_u,
      this.gsplatData.quats,
      this.gsplatData.scales,
      this.gsplatData.sh0,
      this.gsplatData.sh_labels,
      this.gsplatData.sh_centroids,
      ...this.codebookTextures,
    ]
    for (const texture of textures) {
      if (texture) {
        this.renderer.initTexture(texture)
        syncTextureUploadVersion(this.renderer, texture)
      }
    }
  }

  /**
   * 配置材质的 shader defines，参考 PlayCanvas gsplat-sogs-resource.js configureMaterialDefines()。
   *
   * @param defines - 目标 defines 映射，会原地修改
   */
  configureMaterialDefines(defines: Map<string, string>): void {
    // 标记为 SOGS 数据
    defines.set('GSPLAT_SOGS_DATA', '')

    // 设置 SH bands
    defines.set('SH_BANDS', this.gsplatData.shBands.toString())

    // V2 格式标记（启用 Codebook 查找）
    if (this.gsplatData.meta.version === 2) {
      defines.set('GSPLAT_SOGS_V2', '')
    }
  }

  /**
   * 配置材质的 uniforms，参考 PlayCanvas gsplat-sogs-resource.js configureMaterial()。
   *
   * @param material - 目标 gsplat 材质
   */
  configureMaterial(material: IGSplatMaterial): void {
    const { gsplatData } = this
    const { meta } = gsplatData
    const toVector3 = (value: number | number[] | THREE.Vector3): THREE.Vector3 => {
      if (value instanceof THREE.Vector3) return value.clone()
      if (Array.isArray(value))
        return new THREE.Vector3(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0)
      return new THREE.Vector3(value, value, value)
    }
    const toVector4 = (
      value: number | number[] | THREE.Vector4,
      fallbackW: number
    ): THREE.Vector4 => {
      if (value instanceof THREE.Vector4) return value.clone()
      if (Array.isArray(value)) {
        return new THREE.Vector4(
          value[0] ?? 0,
          value[1] ?? value[0] ?? 0,
          value[2] ?? value[0] ?? 0,
          value[3] ?? fallbackW
        )
      }
      return new THREE.Vector4(value, value, value, fallbackW)
    }

    const defines = new Map<string, string>()
    this.configureMaterialDefines(defines)
    material.defines = material.defines || {}
    let definesChanged = false
    if (!defines.has('GSPLAT_SOGS_V2') && 'GSPLAT_SOGS_V2' in material.defines) {
      delete material.defines.GSPLAT_SOGS_V2
      definesChanged = true
    }
    defines.forEach((value, key) => {
      if (material.defines[key] !== value) {
        material.defines[key] = value
        definesChanged = true
      }
    })

    if (!material.uniforms) material.uniforms = {}

    material.uniforms.splatMeansLow = material.uniforms.splatMeansLow || {}
    material.uniforms.splatMeansLow.value = gsplatData.means_l

    material.uniforms.splatMeansHigh = material.uniforms.splatMeansHigh || {}
    material.uniforms.splatMeansHigh.value = gsplatData.means_u

    material.uniforms.splatQuats = material.uniforms.splatQuats || {}
    material.uniforms.splatQuats.value = gsplatData.quats

    material.uniforms.splatScales = material.uniforms.splatScales || {}
    material.uniforms.splatScales.value = gsplatData.scales

    material.uniforms.splatSH0 = material.uniforms.splatSH0 || {}
    material.uniforms.splatSH0.value = gsplatData.sh0

    if (gsplatData.sh_labels) {
      material.uniforms.sogsSH_labels = material.uniforms.sogsSH_labels || {}
      material.uniforms.sogsSH_labels.value = gsplatData.sh_labels
    }
    if (gsplatData.sh_centroids) {
      material.uniforms.packedShN = material.uniforms.packedShN || {}
      material.uniforms.packedShN.value = gsplatData.sh_centroids
    }

    // means 的 mins/maxs
    material.uniforms.sogsMeans_mins = material.uniforms.sogsMeans_mins || {}
    material.uniforms.sogsMeans_mins.value = new THREE.Vector3(...meta.means.mins)

    material.uniforms.sogsMeans_maxs = material.uniforms.sogsMeans_maxs || {}
    material.uniforms.sogsMeans_maxs.value = new THREE.Vector3(...meta.means.maxs)

    // V2 格式：使用 codebook 的最小/最大值
    if (meta.version === 2) {
      // scales codebook — shader 声明为 vec3，需要扩展标量为 Vector3
      if (meta.scales.codebook) {
        const codebook = meta.scales.codebook
        material.uniforms.sogsScales_mins = material.uniforms.sogsScales_mins || {}
        material.uniforms.sogsScales_mins.value = toVector3(
          codebook[0] as number | number[] | THREE.Vector3
        )

        material.uniforms.sogsScales_maxs = material.uniforms.sogsScales_maxs || {}
        material.uniforms.sogsScales_maxs.value = toVector3(
          codebook[255] as number | number[] | THREE.Vector3
        )

        // 创建 codebook 纹理
        const scalesCodebookTexture = this.createCodebookTexture('sogsScalesCodebook', codebook)
        material.uniforms.sogsScalesCodebook = material.uniforms.sogsScalesCodebook || {}
        material.uniforms.sogsScalesCodebook.value = scalesCodebookTexture
      }

      // sh0 codebook — shader 声明为 vec4，需要扩展标量为 Vector4
      if (meta.sh0.codebook) {
        const codebook = meta.sh0.codebook
        material.uniforms.sogsSH0_mins = material.uniforms.sogsSH0_mins || {}
        material.uniforms.sogsSH0_mins.value = toVector4(
          codebook[0] as number | number[] | THREE.Vector4,
          0
        )

        material.uniforms.sogsSH0_maxs = material.uniforms.sogsSH0_maxs || {}
        material.uniforms.sogsSH0_maxs.value = toVector4(
          codebook[255] as number | number[] | THREE.Vector4,
          1
        )

        // 创建 codebook 纹理
        const sh0CodebookTexture = this.createCodebookTexture('sogsSH0Codebook', codebook)
        material.uniforms.sogsSH0Codebook = material.uniforms.sogsSH0Codebook || {}
        material.uniforms.sogsSH0Codebook.value = sh0CodebookTexture
      }

      // shN codebook (如果有)
      if (meta.shN?.codebook) {
        const codebook = meta.shN.codebook
        material.uniforms.sogsSHN_mins = material.uniforms.sogsSHN_mins || {}
        material.uniforms.sogsSHN_mins.value = codebook[0]

        material.uniforms.sogsSHN_maxs = material.uniforms.sogsSHN_maxs || {}
        material.uniforms.sogsSHN_maxs.value = codebook[255]

        // 创建 codebook 纹理
        const shNCodebookTexture = this.createCodebookTexture('sogsSHNCodebook', codebook)
        material.uniforms.sogsSHNCodebook = material.uniforms.sogsSHNCodebook || {}
        material.uniforms.sogsSHNCodebook.value = shNCodebookTexture
      }
    } else {
      // V1 格式：使用 mins/maxs 线性插值
      // 量化范围在元数据里是统一标量，这里扩成 vec3/vec4 以匹配 shader 声明。
      if (meta.scales.mins && meta.scales.maxs) {
        // 取数组中的最小值和最大值作为范围
        const scalesMins = Array.isArray(meta.scales.mins)
          ? Math.min(...meta.scales.mins)
          : meta.scales.mins
        const scalesMaxs = Array.isArray(meta.scales.maxs)
          ? Math.max(...meta.scales.maxs)
          : meta.scales.maxs

        material.uniforms.sogsScales_mins = material.uniforms.sogsScales_mins || {}
        material.uniforms.sogsScales_mins.value = new THREE.Vector3(
          scalesMins,
          scalesMins,
          scalesMins
        )

        material.uniforms.sogsScales_maxs = material.uniforms.sogsScales_maxs || {}
        material.uniforms.sogsScales_maxs.value = new THREE.Vector3(
          scalesMaxs,
          scalesMaxs,
          scalesMaxs
        )
      }

      if (meta.sh0.mins && meta.sh0.maxs) {
        // 取 RGB 三个通道的最小值和最大值
        const sh0Mins = Array.isArray(meta.sh0.mins)
          ? Math.min(...meta.sh0.mins.slice(0, 3))
          : meta.sh0.mins
        const sh0Maxs = Array.isArray(meta.sh0.maxs)
          ? Math.max(...meta.sh0.maxs.slice(0, 3))
          : meta.sh0.maxs

        material.uniforms.sogsSH0_mins = material.uniforms.sogsSH0_mins || {}
        material.uniforms.sogsSH0_mins.value = new THREE.Vector4(sh0Mins, sh0Mins, sh0Mins, 0)

        material.uniforms.sogsSH0_maxs = material.uniforms.sogsSH0_maxs || {}
        material.uniforms.sogsSH0_maxs.value = new THREE.Vector4(sh0Maxs, sh0Maxs, sh0Maxs, 1)
      }

      if (meta.shN?.mins !== undefined && meta.shN?.maxs !== undefined) {
        material.uniforms.sogsSHN_mins = material.uniforms.sogsSHN_mins || {}
        material.uniforms.sogsSHN_mins.value = meta.shN.mins

        material.uniforms.sogsSHN_maxs = material.uniforms.sogsSHN_maxs || {}
        material.uniforms.sogsSHN_maxs.value = meta.shN.maxs
      }
    }

    if (definesChanged) {
      material.needsUpdate = true
    }

    if (!this.loggedConfigureMaterial) {
      // 日志已打印
      this.loggedConfigureMaterial = true
    }
  }

  /**
   * 计算纹理尺寸，参考 PlayCanvas gsplat-sogs-resource.js evalTextureSize()。
   *
   * @returns SOGS 源纹理（means_l）自身宽高
   */
  evalTextureSize(_count: number): TextureSize {
    // SOGS 数据纹理尺寸由加载的 WebP 图像决定
    const { width, height } = getTextureImage(this.gsplatData.means_l)

    return { x: width, y: height }
  }

  /**
   * 创建 Codebook 纹理
   * V2 格式使用 256 个浮点数的查找表
   */
  private createCodebookTexture(name: string, codebook: number[]): THREE.DataTexture {
    const cached = this.codebookTextures.find((texture) => texture.name === name)
    if (cached) {
      return cached
    }

    // Codebook 是 256 个浮点值的数组
    const data = new Float32Array(256)

    for (let i = 0; i < 256; i++) {
      data[i] = codebook[i] ?? 0
    }

    // 创建 1D 纹理（256x1）
    const texture = new THREE.DataTexture(data, 256, 1, THREE.RedFormat, THREE.FloatType)

    texture.name = name
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false
    texture.needsUpdate = true

    // 添加到缓存列表（用于上下文恢复）
    this.codebookTextures.push(texture)

    return texture
  }
}
